// Zebra etiket yazıcılarına (ZD220 vb.) Chrome yazdırma penceresi / Windows sürücüsü olmadan,
// Zebra Browser Print üzerinden ham ZPL ile baskı.
//
// Neden: HTML baskısında sayfa boyutu Chrome tarafından 0.01 inç'e yuvarlanıyor (9mm → 9.144mm,
// toplu baskıda birikmeli dikey kayma) ve sonuç her bilgisayarın sürücü/kenar boşluğu ayarına
// ve Chrome güncellemelerine bağlı (mağazadan mağazaya yatay kayma). ZPL'de etiket ölçüsü,
// medya tipi (aralıklı etiket) ve konum her işte yazıcıya komutla gider; yazıcı her etiketin
// boşluğunu kendisi algılayıp hizalanır.
//
// Etiket, tasarımın yazıcı çözünürlüğünde (203dpi = 8 nokta/mm) birebir çizilmiş 1-bit
// görüntüsü olarak gider (^GFA). Barkod çizgileri tam nokta sayısıyla çizilir (esnetilmez).
import JsBarcode from 'jsbarcode';
import { getLabelAreaPrintOffset, getLabelAlign, getLabelBarcodeFormat, labelBarcodeOptions, DEFAULT_BARCODE_MODULE_MM, getLabelPrintOffsetY } from './labelPrintOffset';

// Önce kendi uygulamamız Qubite Print (qubite-print/), yoksa kuruluysa Zebra Browser Print.
const BP_URLS = ['http://127.0.0.1:9120/', 'http://127.0.0.1:9100/', 'https://127.0.0.1:9101/'];
const SETTINGS_KEY = 'zebraDirectPrint';

export const PRINT_APP_DOWNLOAD_URL = '/downloads';

const GOOGLE_FONT_IDS = ['Roboto Condensed', 'Inter', 'Oswald', 'Noto Sans', 'Montserrat'];
const LABEL_FONTS_GOOGLE_URL = 'https://fonts.googleapis.com/css2?family=Inter:wght@400;600;700&family=Montserrat:wght@400;600;700&family=Noto+Sans:wght@400;600;700&family=Oswald:wght@400;600;700&family=Roboto+Condensed:wght@400;700&display=swap';

const LABEL_FONTS_CSS = {
  'Arial': "Arial, Helvetica, sans-serif",
  'Roboto Condensed': "'Roboto Condensed', Arial, sans-serif",
  'Inter': "'Inter', Arial, sans-serif",
  'Oswald': "'Oswald', Arial, sans-serif",
  'Noto Sans': "'Noto Sans', Arial, sans-serif",
  'Montserrat': "'Montserrat', Arial, sans-serif",
};

// ---- Bu bilgisayara özel ayarlar (yazıcının mekanik farkı cihaza/yazıcıya özeldir) ----

// Yazıcı modeline göre sabit baskı düzeltmesi (mm; x eksi = sola, y eksi = yukarı). Bilgisayarda model
// seçilince uygulanır, böylece farklı modeldeki yazıcı ZD220 ile aynı çıktıyı verir. İnce ayar bunun üstüne eklenir.
export const PRINTER_MODELS = {
  zd220: { label: 'Zebra ZD220', x: 0, y: 0 },
  zd219: { label: 'Zebra ZD219', x: -2.5, y: 0 }, // Yeşilsu: ZD220'ye göre 2-3mm sağa basıyordu (2026-11-02)
};
export const getPrinterModel = (key) => PRINTER_MODELS[key] || PRINTER_MODELS.zd220;

export const getZebraSettings = () => {
  const def = { enabled: true, offsetXmm: 0, offsetYmm: 0, printerUid: '', model: 'zd220' };
  try {
    const s = JSON.parse(localStorage.getItem(SETTINGS_KEY) || '{}');
    return { ...def, ...s };
  } catch {
    return def;
  }
};

export const saveZebraSettings = (patch) => {
  const next = { ...getZebraSettings(), ...patch };
  try { localStorage.setItem(SETTINGS_KEY, JSON.stringify(next)); } catch { /* ignore */ }
  return next;
};

// ---- Zebra Browser Print istemcisi ----

let bpBase = null;

const bpFetch = async (base, path, options = {}, timeoutMs = 3000) => {
  const controller = new AbortController();
  const t = setTimeout(() => controller.abort(), timeoutMs);
  try {
    const res = await fetch(base + path, { ...options, signal: controller.signal });
    if (!res.ok) throw new Error(`Browser Print HTTP ${res.status}`);
    return await res.text();
  } finally {
    clearTimeout(t);
  }
};

const bpRequest = async (path, options, timeoutMs) => {
  if (bpBase) {
    try {
      return await bpFetch(bpBase, path, options, timeoutMs);
    } catch (e) {
      bpBase = null;
    }
  }
  let lastErr;
  for (const base of BP_URLS) {
    try {
      const text = await bpFetch(base, path, options, timeoutMs);
      bpBase = base;
      return text;
    } catch (e) {
      lastErr = e;
    }
  }
  throw lastErr || new Error('Zebra Browser Print bulunamadı');
};

const parseJson = (text) => {
  try { return text ? JSON.parse(text) : null; } catch { return null; }
};

const isZebraDevice = (d) => /zebra|zdesigner|zd2|zd4|zd6|gk4|gc4|gx4|zt/i.test(`${d?.name || ''} ${d?.manufacturer || ''}`);

export const listZebraPrinters = async () => {
  const data = parseJson(await bpRequest('available', { method: 'GET' }));
  const list = data?.printer || [];
  return Array.isArray(list) ? list : [list];
};

// Bu bilgisayarda kullanılacak yazıcı: ayarlarda seçilen → Browser Print varsayılanı → ilk Zebra.
export const getZebraPrinter = async () => {
  const { printerUid } = getZebraSettings();
  let printers = [];
  try { printers = await listZebraPrinters(); } catch (e) { if (!bpBase) throw e; }
  if (printerUid) {
    const chosen = printers.find(p => p.uid === printerUid);
    if (chosen) return chosen;
  }
  const def = parseJson(await bpRequest('default?type=printer', { method: 'GET' }));
  if (def && def.uid) return def;
  return printers.find(isZebraDevice) || printers[0] || null;
};

// Browser Print kurulu ve yazıcı bağlı mı? (Hata fırlatmaz)
export const checkZebraAvailable = async () => {
  if (!getZebraSettings().enabled) return { ok: false, reason: 'disabled' };
  try {
    const printer = await getZebraPrinter();
    if (bpBase === BP_URLS[0] && !getZebraSettings().seenApp) saveZebraSettings({ seenApp: true });
    if (!printer) return { ok: false, reason: 'no-printer' };
    return { ok: true, printer };
  } catch {
    return { ok: false, reason: 'no-browser-print' };
  }
};

// Qubite Print bu bilgisayarda daha önce çalıştıysa ama şu an kapalıysa qubiteprint:// bağlantısıyla
// başlatılır (Qubite Print 1.2.0+ kurulumda kaydeder). Akik Windows uygulamasında window.open
// Windows'a iletilir (shell.openExternal); Chrome'da sayfa değişmeden programı açar (ilk seferde
// Chrome izin sorar, "her zaman izin ver" işaretlenmeli). Tıklama anında çağrılmalı.
const QUBITE_LAUNCH_URL = 'qubiteprint://start';
export const launchQubitePrint = () => {
  try {
    if (/Electron/i.test(navigator.userAgent)) window.open(QUBITE_LAUNCH_URL);
    else window.location.href = QUBITE_LAUNCH_URL;
  } catch { /* ignore */ }
};

// Baskıdan önce: program kapalıysa başlat, açılmasını bekle (en fazla ~12 sn). Hata fırlatmaz.
export const ensureZebraAvailable = async (current, { onLaunching } = {}) => {
  let r = current || await checkZebraAvailable();
  if (r.ok || r.reason !== 'no-browser-print' || !getZebraSettings().seenApp) return r;
  launchQubitePrint();
  if (onLaunching) onLaunching();
  for (let i = 0; i < 24; i++) {
    await new Promise(res => setTimeout(res, 500));
    r = await checkZebraAvailable();
    if (r.ok || r.reason !== 'no-browser-print') return r;
  }
  return r;
};

export const sendZpl = async (printer, zpl) => {
  await bpRequest('write', {
    method: 'POST',
    headers: { 'Content-Type': 'text/plain;charset=UTF-8' },
    body: JSON.stringify({ device: printer, data: zpl }),
  }, 30000);
};

// ---- Yazı tipleri ----

const ensureLabelFonts = async (design) => {
  const families = new Set((design.elements || []).map(el => el.fontFamily).filter(f => GOOGLE_FONT_IDS.includes(f)));
  if (families.size === 0) return;
  if (!document.querySelector('link[data-label-fonts]')) {
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = LABEL_FONTS_GOOGLE_URL;
    link.setAttribute('data-label-fonts', '1');
    document.head.appendChild(link);
    await new Promise(resolve => { link.onload = resolve; link.onerror = resolve; setTimeout(resolve, 4000); });
  }
  if (!document.fonts?.load) return;
  const loads = [];
  families.forEach(f => {
    ['400', '700'].forEach(w => loads.push(document.fonts.load(`${w} 20px '${f}'`, 'AaĞğİıŞşÜüÖöÇç0123456789₺')));
  });
  await Promise.race([Promise.all(loads).catch(() => {}), new Promise(r => setTimeout(r, 4000))]);
};

// ---- Etiket görüntüsü ----

const decodeHtml = (s) => {
  const str = String(s ?? '');
  if (!/[&<]/.test(str)) return str;
  const div = document.createElement('div');
  div.innerHTML = str;
  return div.textContent || '';
};

const getDpmm = (design) => ((parseInt(design?.settings?.printerDpi, 10) || 203) >= 290 ? 12 : 8);

// Code128'de yazıcı tam nokta bastığı için kesirli modül (0.21mm = 1.68 nokta) çizgileri 1/2 nokta karışık yapar
// ve bazı oranlarda barkod hiç okunmaz: 27'li barkodlar tam 18.9mm'de 0/60 okundu, 20mm'de doğru fazla 60/60
// (2026-10-31 ölçümü). Bu yüzden her barkod için tasarım genişliğinden başlayıp okuyucunun çözdüğü
// kenar-kenar oranlarında güvenli pay kalan ilk genişlik + kaydırma seçilir.
const CODE128_MIN_MARGIN = 0.05;
const barcodeModules = (canvas) => {
  const row = canvas.getContext('2d').getImageData(0, 0, canvas.width, 1).data;
  return Array.from({ length: canvas.width }, (_, i) => (row[i * 4] < 128 ? 1 : 0));
};
const toRuns = (bits) => {
  const runs = [];
  let n = 0;
  bits.forEach((b, i) => {
    n += 1;
    if (i === bits.length - 1 || bits[i + 1] !== b) { runs.push(n); n = 0; }
  });
  return runs;
};
const rasterModules = (mods, dotsPerModule, phase) => {
  const out = [];
  const width = Math.ceil(mods.length * dotsPerModule) + 1;
  for (let x = 0; x < width; x++) {
    const i = Math.floor((x + 0.5 + phase) / dotsPerModule);
    out.push(i >= 0 && i < mods.length ? mods[i] : 0);
  }
  const first = out.indexOf(1);
  const last = out.lastIndexOf(1);
  return first < 0 ? [] : out.slice(first, last + 1);
};
// Her karakterin (11 modül, 6 eleman) kenar-kenar mesafesi okuyucunun yuvarlama sınırına ne kadar uzak
const code128Margin = (trueRuns, runs) => {
  if (trueRuns.length !== runs.length) return -1;
  let margin = 1;
  for (let s = 0; s + 6 < runs.length; s += 6) {
    let total = 0;
    for (let k = 0; k < 6; k++) total += runs[s + k];
    for (let k = 0; k < 4; k++) {
      const seen = (runs[s + k] + runs[s + k + 1]) * 11 / total;
      margin = Math.min(margin, 0.5 - Math.abs(seen - (trueRuns[s + k] + trueRuns[s + k + 1])));
    }
  }
  return margin;
};
const code128Raster = (mods, fromDotsPerModule, maxDotsPerModule) => {
  const trueRuns = toRuns(mods);
  for (let dpm = fromDotsPerModule; dpm < maxDotsPerModule; dpm += 0.01) {
    let best = null;
    for (let phase = 0; phase < 1; phase += 0.05) {
      const bits = rasterModules(mods, dpm, phase);
      const margin = code128Margin(trueRuns, toRuns(bits));
      if (!best || margin > best.margin) best = { margin, bits };
    }
    if (best.margin >= CODE128_MIN_MARGIN) return best.bits;
  }
  return rasterModules(mods, maxDotsPerModule, 0);
};

// Genişlik kuralı HTML baskısıyla (labelBarcodeSizeScript) aynı: tam nokta (0.21mm → 2 nokta) kutunun etiket
// içinde kalan kısmına sığıyorsa o, sığmıyorsa tasarımın mm değeri. EAN'da 1.68 noktanın altına inilmez.
// 1 noktaya inilmez: mağaza okuyucusu yanlış okudu (2026-10-27).
// Kutu içinde öğenin hizasına göre (sol/orta/sağ) yerleşir, etiketin dışına taşmaz.
const drawBarcode = (ctx, value, el, design, box, dpmm, moduleMm) => {
  if (!value) return;
  const tmp = document.createElement('canvas');
  try {
    JsBarcode(tmp, String(value), labelBarcodeOptions(value, design, 1, box.h));
  } catch (e) {
    console.error('Barkod hatası:', e);
    return;
  }
  const modules = tmp.width;
  const originX = ctx.getTransform().e;
  const labelW = ctx.canvas.width;
  const cut = Math.max(0, -(box.x + originX), box.x + originX + box.w - labelW);
  const avail = Math.max(0, box.w - 2 * cut);
  const dots = Math.max(1, Math.round(moduleMm * dpmm));
  const fitsWholeDots = modules * dots <= avail + 0.5;
  let source = tmp;
  let width = modules * dots;
  if (!fitsWholeDots) {
    if (getLabelBarcodeFormat(value, design) === 'CODE128') {
      const bits = code128Raster(barcodeModules(tmp), moduleMm * dpmm, dots);
      source = document.createElement('canvas');
      source.width = bits.length;
      source.height = tmp.height;
      const g = source.getContext('2d');
      g.fillStyle = '#fff';
      g.fillRect(0, 0, source.width, source.height);
      g.fillStyle = '#000';
      bits.forEach((b, x) => { if (b) g.fillRect(x, 0, 1, source.height); });
      width = bits.length;
    } else {
      width = Math.max(Math.round(modules * moduleMm * dpmm), Math.ceil(modules * 1.68 * (dpmm / 8)));
    }
  }
  const align = getLabelAlign(el);
  const free = box.w - width;
  let x = Math.round(box.x + (align === 'left' ? 0 : align === 'right' ? free : free / 2));
  x = Math.max(-originX, Math.min(x, labelW - originX - width));
  ctx.save();
  ctx.imageSmoothingEnabled = false;
  ctx.drawImage(source, x, Math.round(box.y + (box.h - tmp.height) / 2), width, tmp.height);
  ctx.restore();
};

const drawText = (ctx, text, el, box, dpmm) => {
  const str = decodeHtml(text);
  if (!str) return;
  const fontCss = (el.fontFamily && LABEL_FONTS_CSS[el.fontFamily])
    || ((el.field === 'barcodeNumber' || el.field === 'stockCode') ? "'Courier New', monospace" : 'Arial, Helvetica, sans-serif');
  const sizePx = (parseFloat(el.fontSize) || 7) * 25.4 / 72 * dpmm;
  ctx.save();
  ctx.beginPath();
  ctx.rect(box.x, box.y, box.w, box.h);
  ctx.clip();
  ctx.font = `${el.fontWeight || 'bold'} ${sizePx}px ${fontCss}`;
  ctx.fillStyle = '#000';
  ctx.textBaseline = 'alphabetic';
  const m = ctx.measureText(str);
  const asc = m.fontBoundingBoxAscent ?? m.actualBoundingBoxAscent ?? sizePx * 0.8;
  const desc = m.fontBoundingBoxDescent ?? m.actualBoundingBoxDescent ?? sizePx * 0.2;
  // HTML'deki gibi: line-height:1 satır kutusu öğe kutusunda dikey ortalı, yazı satırda ortalı
  const baseline = box.y + (box.h - sizePx) / 2 + (sizePx - (asc + desc)) / 2 + asc;
  const align = getLabelAlign(el);
  let x = box.x;
  if (m.width < box.w) {
    if (align === 'center') x = box.x + (box.w - m.width) / 2;
    else if (align === 'right') x = box.x + box.w - m.width;
  }
  ctx.fillText(str, x, baseline);
  ctx.restore();
};

// Tek etiketin 1-bit görüntüsünü çizer. getContent(el) → öğenin metni.
export const renderLabelCanvas = (design, { getContent, barcodeValue }, offset = { x: 0, y: 0 }) => {
  const dm = design.dimensions;
  const dpmm = getDpmm(design);
  const width = Math.round(dm.labelWidth * dpmm);
  const height = Math.round(dm.labelHeight * dpmm);
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  ctx.fillStyle = '#fff';
  ctx.fillRect(0, 0, width, height);
  ctx.save();
  // Tasarımdaki "Baskı Kaydırma" (tüm taslaklar) + bu bilgisayarın yazıcı ince ayarı
  const designShiftX = parseFloat(design.settings?.printOffsetX) || 0;
  const designShiftY = getLabelPrintOffsetY(design);
  ctx.translate(Math.round(((offset.x || 0) + designShiftX) * dpmm), Math.round(((offset.y || 0) + designShiftY) * dpmm));

  const moduleMm = parseFloat(design.settings?.barcodeModuleMm) > 0 ? parseFloat(design.settings.barcodeModuleMm) : DEFAULT_BARCODE_MODULE_MM;
  (design.elements || []).forEach(el => {
    const box = {
      x: (getLabelAreaPrintOffset(el.area, design) + (parseFloat(el.x) || 0)) * dpmm,
      y: (parseFloat(el.y) || 0) * dpmm,
      w: (parseFloat(el.width) || 0) * dpmm,
      h: (parseFloat(el.height) || 0) * dpmm,
    };
    if (el.field === 'barcode' || el.isBarcode) {
      drawBarcode(ctx, barcodeValue, el, design, box, dpmm, moduleMm);
    } else {
      drawText(ctx, getContent(el), el, box, dpmm);
    }
  });
  ctx.restore();

  // Gri (yumuşatılmış) pikselleri siyah/beyaza çevir
  const img = ctx.getImageData(0, 0, width, height);
  const d = img.data;
  for (let i = 0; i < d.length; i += 4) {
    const lum = (d[i] * 299 + d[i + 1] * 587 + d[i + 2] * 114) / 1000;
    const v = lum < 150 ? 0 : 255;
    d[i] = d[i + 1] = d[i + 2] = v;
    d[i + 3] = 255;
  }
  ctx.putImageData(img, 0, 0);
  return canvas;
};

// ---- ZPL ----

// ZPL ASCII sıkıştırması için tekrar sayısı kodu (G..Y = 1..19, g..z = 20..400)
const repeatCode = (n) => {
  let out = '';
  while (n > 0) {
    if (n >= 20) {
      const k = Math.min(20, Math.floor(n / 20));
      out += String.fromCharCode('g'.charCodeAt(0) + k - 1);
      n -= k * 20;
    } else {
      out += String.fromCharCode('G'.charCodeAt(0) + n - 1);
      n = 0;
    }
  }
  return out;
};

const compressRow = (hex) => {
  if (/^0+$/.test(hex)) return ',';
  if (/^F+$/.test(hex)) return '!';
  const trimmedZeros = hex.replace(/0+$/, '');
  const tail = trimmedZeros.length < hex.length ? ',' : '';
  let out = '';
  let i = 0;
  while (i < trimmedZeros.length) {
    const c = trimmedZeros[i];
    let j = i + 1;
    while (j < trimmedZeros.length && trimmedZeros[j] === c) j++;
    const n = j - i;
    out += n > 1 ? repeatCode(n) + c : c;
    i = j;
  }
  return out + tail;
};

// Canvas → ^GFA (siyah = 1)
export const canvasToZplGraphic = (canvas) => {
  const { width, height } = canvas;
  const bytesPerRow = Math.ceil(width / 8);
  const data = canvas.getContext('2d').getImageData(0, 0, width, height).data;
  const rows = [];
  let prev = null;
  for (let y = 0; y < height; y++) {
    let hex = '';
    for (let b = 0; b < bytesPerRow; b++) {
      let byte = 0;
      for (let bit = 0; bit < 8; bit++) {
        const x = b * 8 + bit;
        if (x < width && data[(y * width + x) * 4] === 0) byte |= (0x80 >> bit);
      }
      hex += byte.toString(16).toUpperCase().padStart(2, '0');
    }
    rows.push(hex === prev ? ':' : compressRow(hex));
    prev = hex;
  }
  const total = bytesPerRow * height;
  return `^GFA,${total},${total},${bytesPerRow},${rows.join('')}`;
};

// Her işte yazıcının durumunu sıfırdan kurar: sürücünün bıraktığı kaydırma/yön/medya ayarları
// bu baskıyı etkilemez. ^MNY = aralıklı etiket (boşluk algılama), ^MMT = koparma modu,
// ^PR2 = en düşük hız (küçük etikette en sağlam besleme), ^PQ = adet.
// shiftX/shiftY (nokta): bu yazıcının model + ince ayar düzeltmesi, görüntü içinde değil yazıcı komutuyla
// uygulanır — etiketin tamamı birlikte kayar, kenardaki barkod kesilmez / takılmaz.
// Yatay: ZD serisi (ortalayan kılavuzlu) baskı genişliğini (^PW) etiket ortasına yerleştirir; genişlik iki
// yandan |shiftX| büyütülür, görüntü sola kaydırmada en başa, sağa kaydırmada 2×|shiftX| içeri konur.
// Dikey: ^LT (−120..120 nokta).
export const buildLabelZpl = ({ graphic, widthDots, heightDots, qty = 1, shiftX = 0, shiftY = 0 }) => [
  '^XA',
  '^MNY', '^MMT', '^PR2,2,2', '^PON', '^PMN', '^LRN', '^LH0,0', '^LS0',
  `^LT${Math.max(-120, Math.min(120, shiftY))}`,
  `^PW${widthDots + 2 * Math.abs(shiftX)}`, `^LL${heightDots}`,
  `^FO${shiftX > 0 ? 2 * shiftX : 0},0${graphic}^FS`,
  `^PQ${Math.max(1, qty)},0,1,Y`,
  '^XZ',
].join('\n');

// Kalibrasyon: medya tipini aralıklı etiket yap, etiket boyunu öğret, kalıcı kaydet.
export const buildCalibrationZpl = (design) => {
  const dm = design?.dimensions || { labelWidth: 70, labelHeight: 9 };
  const dpmm = getDpmm(design);
  return [
    `^XA^MNY^PON^LH0,0^LS0^LT0^PW${Math.round(dm.labelWidth * dpmm)}^LL${Math.round(dm.labelHeight * dpmm)}^JUS^XZ`,
    '~JC',
  ].join('\n');
};

// Bu bilgisayarın yazıcı modeli + ince ayarı, nokta cinsinden
export const getDeviceShiftMm = () => {
  const st = getZebraSettings();
  const m = getPrinterModel(st.model);
  return { x: (parseFloat(st.offsetXmm) || 0) + m.x, y: (parseFloat(st.offsetYmm) || 0) + m.y };
};
const getDeviceShiftDots = (design) => {
  const dpmm = getDpmm(design);
  const mm = getDeviceShiftMm();
  return { x: Math.round(mm.x * dpmm), y: Math.round(mm.y * dpmm) };
};

// labels: [{ getContent(el), barcodeValue, qty }] — aynı ürünün ardışık etiketleri tek
// görüntü + ^PQ ile gönderilir. Parçalar halinde yollanır (büyük işlerde Browser Print'i boğmamak için).
export const printLabelsZebra = async (design, labels, { printer, onProgress } = {}) => {
  const target = printer || await getZebraPrinter();
  if (!target) throw new Error('Zebra yazıcı bulunamadı');
  await ensureLabelFonts(design);
  const shift = getDeviceShiftDots(design);

  const chunks = [];
  let chunk = '';
  let total = 0;
  labels.forEach(l => {
    const canvas = renderLabelCanvas(design, l);
    chunk += buildLabelZpl({ graphic: canvasToZplGraphic(canvas), widthDots: canvas.width, heightDots: canvas.height, qty: l.qty, shiftX: shift.x, shiftY: shift.y }) + '\n';
    total += Math.max(1, l.qty || 1);
    if (chunk.length > 200000) { chunks.push(chunk); chunk = ''; }
  });
  if (chunk) chunks.push(chunk);

  for (let i = 0; i < chunks.length; i++) {
    await sendZpl(target, chunks[i]);
    if (onProgress) onProgress(i + 1, chunks.length);
  }
  return { printer: target, count: total };
};
